import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ensureProfileForUser, fetchPersonNameByEmail } from "@/api/profile";
import { fetchIsAdmin, fetchMyHostLocationIds } from "@/api/roles";
import {
  clearImpersonation,
  hasImpersonationRecord,
} from "@/lib/impersonation";
import { logger, serializeError } from "@/lib/logger";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  /** Location IDs the signed-in user hosts. Empty for non-hosts. */
  hostLocationIds: number[];
  /** True when the user is an admin OR has any host assignment. */
  isHost: boolean;
  /** Preferred display name: edited person.first_name + last_name, falling
   *  back to Google full_name, then email. */
  displayName: string | null;
  /** First name from the linked Quizrun person profile. */
  firstName: string | null;
  /** Quizrun person table ID (integer), null if not linked. */
  personId: number | null;
  /** Re-fetch the person row (call after editing name on the profile page). */
  refreshProfile: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<SignUpResult>;
  /** Email a password-reset link that lands on /reset-password. */
  resetPassword: (email: string) => Promise<void>;
  /** Global sign-in dialog (Google + email/password), rendered once in App. */
  authModalOpen: boolean;
  /** Screen the dialog opens on: sign-in/sign-up tabs, or "forgot password". */
  authModalView: AuthModalView;
  setAuthModalOpen: (open: boolean, view?: AuthModalView) => void;
  signOut: () => Promise<void>;
}

export type AuthModalView = "auth" | "forgot";

/**
 * - "confirm": confirmation email sent — OR the email is already registered.
 *              Supabase deliberately returns the same response for both (and
 *              sends no email for the latter) so sign-up can't be used to
 *              probe which emails have accounts. Don't tell them apart.
 * - "signed_in": project has email confirmation off, session issued directly.
 */
export type SignUpResult = "confirm" | "signed_in";

/** Current URL minus the hash — a hash in the redirect yields a malformed
 *  '##access_token=…' that detectSessionInUrl can't parse (see #195). */
const currentUrlWithoutHash = () =>
  window.location.origin + window.location.pathname + window.location.search;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hostLocationIds, setHostLocationIds] = useState<number[]>([]);
  const [personName, setPersonName] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [personId, setPersonId] = useState<number | null>(null);
  const [authModal, setAuthModal] = useState<{
    open: boolean;
    view: AuthModalView;
  }>({
    open: false,
    view: "auth",
  });
  const setAuthModalOpen = useCallback(
    (open: boolean, view: AuthModalView = "auth") =>
      setAuthModal({ open, view }),
    [],
  );

  // Stable refs so callbacks don't invalidate the auth-listener effect.
  const rolesCacheRef = useRef<
    Map<string, { admin: boolean; hostLocationIds: number[] }>
  >(new Map());
  const userEmailRef = useRef<string | null>(null);
  const authUserIdRef = useRef<string | null>(null);
  const syncingRef = useRef(false);
  // Bumped per sync; a late auth result checks it to detect it's stale.
  const syncGenRef = useRef(0);

  const loadPersonName = useCallback(async (email: string) => {
    try {
      const person = await fetchPersonNameByEmail(email);
      if (!person) {
        setPersonId(null);
        setPersonName(null);
        setFirstName(null);
        return;
      }
      setPersonId(person.person_id);
      setFirstName(person.first_name);
      const full = [person.first_name, person.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
      setPersonName(full || null);
    } catch (err) {
      logger.error("Error fetching person name", serializeError(err));
      setPersonName(null);
      setFirstName(null);
      setPersonId(null);
    }
  }, []);

  const loadRoles = useCallback(async (rawEmail: string) => {
    const email = rawEmail.toLowerCase();
    const cached = rolesCacheRef.current.get(email);
    if (cached) {
      setIsAdmin(cached.admin);
      setHostLocationIds(cached.hostLocationIds);
      return;
    }
    setRolesLoading(true);
    try {
      const [admin, hostIds] = await Promise.all([
        fetchIsAdmin(),
        fetchMyHostLocationIds(),
      ]);
      rolesCacheRef.current.set(email, { admin, hostLocationIds: hostIds });
      setIsAdmin(admin);
      setHostLocationIds(hostIds);
    } catch (err) {
      logger.error("Error loading roles", serializeError(err));
      setIsAdmin(false);
      setHostLocationIds([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    const hydrate = (email: string | null) => {
      userEmailRef.current = email;
      if (email) {
        loadRoles(email);
        loadPersonName(email);
      } else {
        setIsAdmin(false);
        setHostLocationIds([]);
        setPersonName(null);
        setFirstName(null);
        setPersonId(null);
      }
    };

    // resume=true: refresh the token (a slept tab's stored token is expired).
    const syncSession = async (initial = false, resume = false) => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      const gen = ++syncGenRef.current;
      const isStale = () => syncGenRef.current !== gen;
      try {
        // Timeout race: auth calls can hang forever on suspended mobile tabs.
        const authCall = resume
          ? supabase.auth.refreshSession()
          : supabase.auth.getSession();
        const result = await Promise.race([
          authCall,
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 8000)),
        ]);

        if (!result) {
          // Timed out but authCall is still in flight. Bump gen so its late result
          // is stale-detectable, and guard it (else a late success re-signs-in a
          // dropped user / a late rejection goes unhandled).
          syncGenRef.current++;
          void authCall
            .then((late) => {
              // resume already dropped to anon → kill any late session locally.
              if (resume && (late.error || late.data.session)) {
                void supabase.auth.signOut({ scope: "local" }).catch(() => {});
              }
            })
            .catch(() => {});

          if (resume) {
            // refresh timed out → token almost certainly expired; go anonymous.
            setUser(null);
            hydrate(null);
            void supabase.auth.signOut({ scope: "local" }).catch(() => {});
          }
          // initial getSession timeout: don't sign out (session may be valid).
          return;
        }

        if (isStale()) return; // a newer sync started while awaiting

        if (resume && (result.error || !result.data.session)) {
          setUser(null);
          hydrate(null);
          void supabase.auth.signOut({ scope: "local" }).catch(() => {});
        } else {
          const session = result.data.session;
          setUser(session?.user ?? null);
          hydrate(session?.user?.email ?? null);
        }
      } catch (err) {
        if (isStale()) return;
        logger.error("session sync failed — clearing stale auth", serializeError(err));
        setUser(null);
        hydrate(null);
        void supabase.auth.signOut({ scope: "local" }).catch(() => {});
      } finally {
        syncingRef.current = false;
        if (initial) setSessionLoading(false);
      }
    };

    void syncSession(true);

    // Pause auto-refresh while hidden (else the token expires asleep);
    // refresh on return.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void supabase.auth.startAutoRefresh();
        void syncSession(false, true);
      } else {
        void supabase.auth.stopAutoRefresh();
      }
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // bfcache restore can skip visibilitychange.
        void supabase.auth.startAutoRefresh();
        void syncSession(false, true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handlePageShow);

    // Keep synchronous — returning a promise can deadlock session restore.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUserId = session?.user?.id ?? null;
      if (authUserIdRef.current !== nextUserId) {
        authUserIdRef.current = nextUserId;
        // Drop the previous user's data but keep mounted queries alive (clear() strands them).
        void queryClient.resetQueries();
      }
      setUser(session?.user ?? null);
      hydrate(session?.user?.email ?? null);
      if (event === "SIGNED_IN" && session?.user && !hasImpersonationRecord()) {
        const email = session.user.email;
        // new user: re-fetch name after the row exists, else personId stays null.
        // Guard on the live email so a sign-out mid-provision can't repopulate a
        // stale session.
        ensureProfileForUser(session.user)
          .then(() => {
            if (email && userEmailRef.current === email)
              void loadPersonName(email);
          })
          .catch((err) => logger.error("ensureProfileForUser failed", serializeError(err)));
      }
      if (event === "TOKEN_REFRESHED") {
        // New token — refetch data fetched with the old one.
        void queryClient.invalidateQueries();
      }
    });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handlePageShow);
      subscription.unsubscribe();
    };
  }, [loadRoles, loadPersonName, queryClient]);

  const refreshProfile = useCallback(async () => {
    const email = userEmailRef.current;
    if (email) await loadPersonName(email);
  }, [loadPersonName]);

  const displayName =
    personName ??
    (user?.user_metadata?.full_name as string | undefined) ??
    (user?.user_metadata?.name as string | undefined) ??
    user?.email ??
    null;

  const signInWithGoogle = async () => {
    // Strip only the URL hash (keep query) so deep-link state (e.g. ?tab=past,
    // ?event_id=) survives the round-trip.
    const redirectTo = currentUrlWithoutHash();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      // Always show Google's account picker so users can choose/confirm which
      // account (or add another) instead of being silently logged in with the
      // browser's active session.
      options: { redirectTo, queryParams: { prompt: "select_account" } },
    });
    if (error) {
      logger.error("Error signing in with Google", serializeError(error));
      throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) throw error;
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
  ): Promise<SignUpResult> => {
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      // Confirmation link returns to the page the user signed up from. Must be
      // in Supabase Auth's redirect allow-list, else it falls back to Site URL.
      options: { emailRedirectTo: currentUrlWithoutHash() },
    });
    if (error) throw error;
    return data.session ? "signed_in" : "confirm";
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin + "/reset-password",
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const isViewingAs = hasImpersonationRecord();
    const { error } = await supabase.auth.signOut({
      scope: isViewingAs ? "local" : "global",
    });
    if (error) {
      logger.error("Error signing out", serializeError(error));
      throw error;
    }
    setIsAdmin(false);
    setHostLocationIds([]);
    setPersonName(null);
    setFirstName(null);
    setPersonId(null);
    if (isViewingAs) {
      clearImpersonation();
      window.location.assign("/");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading: sessionLoading || rolesLoading,
        isAdmin,
        hostLocationIds,
        isHost: isAdmin || hostLocationIds.length > 0,
        displayName,
        firstName,
        personId,
        refreshProfile,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        resetPassword,
        authModalOpen: authModal.open,
        authModalView: authModal.view,
        setAuthModalOpen,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
